import type { UseComboboxPropGetters } from "downshift";
import { useCombobox, useMultipleSelection } from "downshift";
import type { ReactNode } from "react";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { twJoin } from "tailwind-merge";

import checkIcon from "@/assets/icons/check.svg";
import chevronDownIcon from "@/assets/icons/chevron-down.svg";
import xCloseIcon from "@/assets/icons/x-close.svg";
import { Badge } from "@/design-system/Badge";
import { Tooltip } from "@/design-system/Tooltip";
import { autoCompleteDisabledProperties } from "@/helpers/auto-complete-helpers";
import { twResolve } from "@/helpers/tw-resolve";
import { useSelectFloating } from "@/ui-adapters/useSelectFloating";

import { fieldControlStateVariants } from "./field-control";

export interface MultiSelectProps<T> {
  placeholder?: string;
  selected: T[] | undefined;
  unclearable?: T[];
  disabled?: boolean;
  items: T[];
  renderOption: (item: T, selected: boolean) => ReactNode;
  keySelector: (item: T) => string | number;
  onChange?: (items: T[]) => void;
  onBlur?: () => void;
  disabledItems?: T[];
  disabledItemTooltip?: string;
  searchableFieldSelector?: (item: T) => string[];
  sortSelectedItems?: (a: T, b: T) => number;
  "aria-invalid"?: boolean;
  id?: string;
  usePortal?: boolean;
}

/** Renders a typed multi-value select whose options can be filtered with free text. */
export function MultiSelect<T>({
  selected = [],
  unclearable = [],
  disabled,
  placeholder,
  items,
  renderOption,
  keySelector,
  onChange,
  onBlur,
  disabledItems = [],
  disabledItemTooltip,
  searchableFieldSelector,
  sortSelectedItems,
  "aria-invalid": isInvalid,
  usePortal = false,
  ...props
}: MultiSelectProps<T>): React.ReactNode {
  const [inputValue, setInputValue] = useState("");
  const selectedItemsContainerRef = useRef<HTMLDivElement | null>(null);
  const {
    getSelectedItemProps,
    getDropdownProps,
    setSelectedItems,
    addSelectedItem,
    removeSelectedItem,
    selectedItems,
  } = useMultipleSelection({
    selectedItems: selected,
    defaultActiveIndex: 0,
    onSelectedItemsChange: (stateChange) => {
      onChange?.(stateChange.selectedItems ?? []);
      // Close menu if all items are selected
      if (stateChange.selectedItems.length === items.length) {
        closeMenu();
      }
    },
    onStateChange: ({ type, selectedItems: newSelectedItems = [] }) => {
      switch (type) {
        case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownBackspace:
        case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownDelete:
        case useMultipleSelection.stateChangeTypes.DropdownKeyDownBackspace:
        case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
          setSelectedItems(newSelectedItems);
          break;
        default:
          break;
      }
    },
  });
  const filteredItems = useMemo(
    () =>
      items.filter((item) => {
        if (selectedItems.some((selectedItem) => keySelector(selectedItem) === keySelector(item))) {
          return false;
        }
        if (searchableFieldSelector) {
          return searchableFieldSelector(item).some((field) => field.toLowerCase().includes(inputValue.toLowerCase()));
        }

        const renderedResult = renderOption(item, false);
        if (typeof renderedResult === "string") {
          return renderedResult.toLowerCase().includes(inputValue.toLowerCase());
        }

        return false;
      }),
    [inputValue, items, keySelector, searchableFieldSelector, renderOption, selectedItems],
  );
  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getInputProps,
    getLabelProps,
    highlightedIndex,
    getItemProps,
    closeMenu,
  } = useCombobox({
    inputValue,
    defaultHighlightedIndex: 0,
    selectedItem: null,
    items: filteredItems,
    stateReducer: (state, { type, changes }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputBlur:
          return {
            ...changes,
            ...(changes.selectedItem
              ? {
                  isOpen: true,
                  highlightedIndex:
                    filteredItems.length <= state.highlightedIndex + 1
                      ? filteredItems.length - 2
                      : state.highlightedIndex,
                }
              : null),
          };
        default:
          return changes;
      }
    },
    onStateChange: ({ inputValue, type, selectedItem }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputChange:
          setInputValue(inputValue || "");
          break;
        case useCombobox.stateChangeTypes.InputBlur:
          setInputValue("");
          onBlur?.();
          break;
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          if (selectedItem && !disabledItems.includes(selectedItem)) {
            setInputValue("");
            if (selectedItems.includes(selectedItem)) {
              removeSelectedItem(selectedItem);
            } else {
              addSelectedItem(selectedItem);
            }
          }
          break;
        default:
          break;
      }
    },
  });
  const sortedSelectedItems = useMemo(() => {
    if (sortSelectedItems) {
      return selectedItems.toSorted(sortSelectedItems);
    }

    return selectedItems;
  }, [selectedItems, sortSelectedItems]);

  useEffect(() => {
    if (!selectedItemsContainerRef.current) {
      return;
    }

    selectedItemsContainerRef.current.scrollTop = selectedItemsContainerRef.current.scrollHeight;
  }, [sortedSelectedItems]);

  const { referenceRef, menuRef, menuProps, opensAbove } = useSelectFloating({ open: isOpen, usePortal });

  const setSelectedItemsContainerRef = (node: HTMLDivElement | null) => {
    selectedItemsContainerRef.current = node;
    referenceRef(node);
  };

  const menuContent = isOpen
    ? filteredItems.map((item, index) => (
        <ListItem<T>
          key={keySelector(item)}
          renderOption={renderOption}
          selectedItems={selectedItems}
          item={item}
          index={index}
          isHighlighted={highlightedIndex === index}
          getItemProps={getItemProps}
          disabled={disabledItems.includes(item)}
          disabledTooltip={disabledItemTooltip}
        />
      ))
    : null;

  const menu = (
    <ul
      {...getMenuProps({ ref: menuRef }, { suppressRefError: !isOpen })}
      {...menuProps}
      data-portal-dropdown={usePortal || undefined}
      className={twJoin(
        "z-select-menu flex w-max cursor-default flex-col items-start overflow-auto rounded-lg border border-border-brand-secondary bg-background-default outline-hidden! transition-colors",
        opensAbove ? "rounded-b-none" : "rounded-t-none",
        usePortal ? "shadow-overlay" : "shadow-tooltip",
      )}
    >
      {menuContent}
    </ul>
  );

  return (
    <div
      className={twResolve("relative", isOpen && "z-20", disabled && "pointer-events-none")}
      data-testid="multi-select"
    >
      <div>
        <div
          {...getLabelProps()}
          ref={setSelectedItemsContainerRef}
          className={twResolve(
            "relative flex max-h-multi-select-values min-h-control-md w-full cursor-default items-start overflow-y-auto rounded-lg border border-border-secondary bg-background-default pr-searchable-select-adornment pl-select-option-inline text-left leading-select-trigger",
            isOpen && "border-border-brand-secondary",
            isOpen && (opensAbove ? "rounded-t-none" : "rounded-b-none"),
            fieldControlStateVariants({ invalid: isInvalid, focus: "within" }),
            disabled && "bg-background-disabled",
          )}
        >
          <div className="flex w-full flex-wrap items-center gap-multi-select-value py-select-option-block">
            {sortedSelectedItems.map((selectedItem, index) => {
              const isDisabled = disabled || unclearable.includes(selectedItem);

              return (
                <button
                  key={`selected-${keySelector(selectedItem)}`}
                  {...getSelectedItemProps({
                    selectedItem,
                    index,
                  })}
                  type="button"
                  className="max-w-full not-disabled:cursor-pointer focus:outline-hidden"
                  onClick={() => removeSelectedItem(selectedItem)}
                  disabled={isDisabled}
                >
                  <Badge variant="white" clickable={!isDisabled}>
                    <div
                      className={twResolve(
                        "flex max-w-full items-center gap-space-xs truncate text-text-on-default-secondary",
                        isDisabled && "text-text-on-disabled",
                      )}
                    >
                      {renderOption(selectedItem, true)}
                      {!isDisabled && <MultiSelectIcon name={xCloseIcon} size={12} />}
                    </div>
                  </Badge>
                </button>
              );
            })}
            <input
              {...getInputProps(
                getDropdownProps({
                  preventKeyAction: isOpen,
                }),
              )}
              disabled={disabled}
              aria-invalid={isInvalid}
              placeholder={!selectedItems.length ? placeholder : ""}
              className="h-full w-multi-select-input-collapsed min-w-multi-select-input-min flex-1 py-select-option-block text-body placeholder:text-text-tertiary focus:outline-hidden"
              {...autoCompleteDisabledProperties}
            />
          </div>
          <button
            {...getToggleButtonProps({
              type: "button",
            })}
            id={props.id}
            className="absolute top-0 right-0 flex h-full w-searchable-select-adornment items-center justify-center focus:outline-hidden"
            aria-label="toggle menu"
            data-testid="multi-select-toggle"
          >
            <span className={twResolve("flex items-center text-text-secondary", disabled && "text-text-on-disabled")}>
              <MultiSelectIcon name={chevronDownIcon} animateRotation rotate={isOpen} />
            </span>
          </button>
        </div>
      </div>

      {isOpen && (usePortal ? createPortal(menu, document.body) : menu)}
    </div>
  );
}

function MultiSelectIcon({
  name,
  size = 16,
  animateRotation = false,
  rotate = false,
}: {
  name: string;
  size?: number;
  animateRotation?: boolean;
  rotate?: boolean;
}): React.ReactNode {
  return (
    <span
      className={twJoin("shrink-0 fill-transparent", animateRotation && "transition-transform", rotate && "rotate-180")}
    >
      <svg aria-hidden="true" width={size} height={size} data-testid={name}>
        <use xlinkHref={`#${name}`} />
      </svg>
    </span>
  );
}
const ListItem = memo(ListItemInner) as typeof ListItemInner;
function ListItemInner<T>(props: {
  item: T;
  index: number;
  isHighlighted: boolean;
  getItemProps: UseComboboxPropGetters<T>["getItemProps"];
  renderOption: MultiSelectProps<T>["renderOption"];
  selectedItems: T[];
  disabled: boolean;
  disabledTooltip?: string;
}) {
  const { item, index, isHighlighted, getItemProps, renderOption, selectedItems, disabled, disabledTooltip } = props;
  const isSelected = selectedItems.includes(item);

  return (
    <li
      data-testid="multi-select-item"
      {...getItemProps({
        index,
        item,
      })}
      className={twJoin(
        "w-full cursor-pointer px-select-option-inline py-select-option-block text-text-primary select-none first:mt-select-menu-edge last:mb-select-menu-edge",
        isHighlighted && "bg-background-brand-light",
        disabled && "cursor-default text-text-on-disabled",
      )}
    >
      <Tooltip tooltip={disabled && disabledTooltip}>
        <div className={twJoin("flex items-center justify-between gap-space-sm", isSelected && "text-body-emphasis")}>
          {renderOption(item, selectedItems.includes(item))}
          {isSelected ? <MultiSelectIcon name={checkIcon} /> : null}
        </div>
      </Tooltip>
    </li>
  );
}
